import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createJob, type Brand, type IntendedUse, type Brief } from '../api';

const BRAND_OPTIONS: { value: Brand; label: string }[] = [
  { value: 'Calitoy', label: 'Calitoy' },
  { value: 'Kurced', label: 'Kurced' },
  { value: 'CalitoyMuse', label: 'CalitoyMuse' },
  { value: 'Custom', label: 'Custom' },
];

const INTENDED_USES: { value: IntendedUse; label: string; desc: string }[] = [
  { value: 'chest_mark', label: 'Chest Mark', desc: 'Small logo on left chest' },
  { value: 'full_front', label: 'Full Front', desc: 'Large print covering front' },
  { value: 'back_print', label: 'Back Print', desc: 'Full back design' },
  { value: 'sleeve', label: 'Sleeve', desc: 'Small sleeve print' },
  { value: 'embroidery', label: 'Embroidery', desc: 'Stitched logo' },
  { value: 'sticker', label: 'Sticker', desc: 'Die-cut sticker design' },
];

const GARMENT_TYPES = ['t-shirt', 'hoodie', 'long-sleeve', 'tank-top', 'sweatshirt'];
const GARMENT_COLORS = ['Black', 'White', 'Wine', 'Navy', 'Sand', 'Gold', 'Forest', 'Heather Grey'];

export function NewDesign() {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const [brand, setBrand] = useState<Brand>('Calitoy');
  const [customBrand, setCustomBrand] = useState('');
  const [logoPrompt, setLogoPrompt] = useState('');
  const [referenceImage, setReferenceImage] = useState('');
  const [requiredText, setRequiredText] = useState('');
  const [style, setStyle] = useState('');
  const [colors, setColors] = useState<string[]>([]);
  const [exclusions, setExclusions] = useState<string[]>([]);
  const [intendedUse, setIntendedUse] = useState<IntendedUse>('chest_mark');
  const [garmentTypes, setGarmentTypes] = useState<string[]>([]);
  const [garmentColors, setGarmentColors] = useState<string[]>([]);
  const [conceptCount, setConceptCount] = useState(4);
  const [error, setError] = useState('');

  function toggleArrayValue(arr: string[], value: string): string[] {
    return arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];
  }

  function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) {
      // In real app, this would upload to a server
      // For mock, use a placeholder URL
      setReferenceImage(`https://via.placeholder.com/400x400/14110f/c9a96e?text=Reference`);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!logoPrompt.trim()) {
      setError('Logo prompt is required');
      return;
    }

    setSubmitting(true);
    setError('');

    const effectiveBrand = brand === 'Custom' ? (customBrand.trim() || 'Custom') : brand;

    const brief: Brief = {
      brand: effectiveBrand as Brand,
      logoPrompt,
      referenceImage: referenceImage || undefined,
      requiredText: requiredText || undefined,
      style: style || undefined,
      colors: colors.length > 0 ? colors : undefined,
      exclusions: exclusions.length > 0 ? exclusions : undefined,
      intendedUse,
      garmentTypes: garmentTypes.length > 0 ? garmentTypes : undefined,
      garmentColors: garmentColors.length > 0 ? garmentColors : undefined,
      conceptCount,
    };

    try {
      const job = await createJob(brief);
      navigate(`/job/${job.id}`);
    } catch {
      setError('Failed to create job. Please try again.');
      setSubmitting(false);
    }
  }

  return (
    <div style={{ maxWidth: '800px' }}>
      <h1 style={{ marginBottom: '24px' }}>New Design Brief</h1>

      <form onSubmit={handleSubmit}>
        {/* Brand Selector */}
        <div className="panel">
          <div className="panel__header">
            <h3 className="panel__title">Brand</h3>
          </div>
          <div className="form-group">
            <label className="form-label">Select Brand <span className="form-label__required">*</span></label>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              {BRAND_OPTIONS.map((b) => (
                <button
                  key={b.value}
                  type="button"
                  className={`filter-chip ${brand === b.value ? 'filter-chip--active' : ''}`}
                  onClick={() => setBrand(b.value)}
                >
                  {b.label}
                </button>
              ))}
            </div>
          </div>
          {brand === 'Custom' && (
            <div className="form-group">
              <label className="form-label">Custom Brand Name</label>
              <input
                className="form-input"
                value={customBrand}
                onChange={(e) => setCustomBrand(e.target.value)}
                placeholder="Enter brand name"
              />
            </div>
          )}
        </div>

        {/* Artwork Brief */}
        <div className="panel">
          <div className="panel__header">
            <h3 className="panel__title">Artwork Brief</h3>
          </div>
          <div className="form-group">
            <label className="form-label">Logo Prompt <span className="form-label__required">*</span></label>
            <textarea
              className="form-textarea"
              value={logoPrompt}
              onChange={(e) => setLogoPrompt(e.target.value)}
              placeholder="Describe the logo or design you want to generate..."
              rows={4}
            />
            <p className="form-hint">Be specific about composition, elements, and mood. The AI will use this as the core instruction.</p>
          </div>

          <div className="form-group">
            <label className="form-label">Reference Image (optional)</label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              style={{ fontSize: '0.8rem', color: 'var(--ink-muted)' }}
            />
            {referenceImage && (
              <div style={{ marginTop: '8px' }}>
                <img src={referenceImage} alt="Reference" style={{ maxWidth: '120px', borderRadius: '4px', border: '1px solid var(--border)' }} />
                <button type="button" className="btn btn--ghost btn--sm" style={{ marginLeft: '8px' }} onClick={() => setReferenceImage('')}>
                  Remove
                </button>
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Text That Must Appear</label>
            <input
              className="form-input"
              value={requiredText}
              onChange={(e) => setRequiredText(e.target.value)}
              placeholder="e.g., CALITOY, brand name, tagline"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Style</label>
              <input
                className="form-input"
                value={style}
                onChange={(e) => setStyle(e.target.value)}
                placeholder="e.g., vintage heraldic, street graffiti, minimalist"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Number of Concepts</label>
              <select
                className="form-select"
                value={conceptCount}
                onChange={(e) => setConceptCount(parseInt(e.target.value))}
              >
                {[1, 2, 3, 4, 5, 6].map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Colors</label>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {['Gold', 'Black', 'Wine', 'White', 'Navy', 'Forest', 'Silver', 'Bronze'].map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`filter-chip ${colors.includes(c) ? 'filter-chip--active' : ''}`}
                  onClick={() => setColors(toggleArrayValue(colors, c))}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Exclusions (avoid)</label>
            <input
              className="form-input"
              value={exclusions.join(', ')}
              onChange={(e) => setExclusions(e.target.value.split(',').map((s) => s.trim()).filter(Boolean))}
              placeholder="e.g., neon, cartoon, photorealistic"
            />
            <p className="form-hint">Comma-separated list of things to avoid in generation.</p>
          </div>
        </div>

        {/* Intended Use */}
        <div className="panel">
          <div className="panel__header">
            <h3 className="panel__title">Intended Use</h3>
          </div>
          <div className="form-group">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '8px' }}>
              {INTENDED_USES.map((u) => (
                <div
                  key={u.value}
                  onClick={() => setIntendedUse(u.value)}
                  style={{
                    padding: '12px',
                    border: `1px solid ${intendedUse === u.value ? 'var(--gold)' : 'var(--border-light)'}`,
                    borderRadius: 'var(--radius-sm)',
                    cursor: 'pointer',
                    background: intendedUse === u.value ? 'rgba(201,169,110,0.08)' : 'var(--bg-base)',
                    transition: 'all var(--transition)',
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: '0.85rem', color: intendedUse === u.value ? 'var(--gold)' : 'var(--ink)' }}>
                    {u.label}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--ink-faint)', marginTop: '2px' }}>
                    {u.desc}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Garment Preferences */}
        <div className="panel">
          <div className="panel__header">
            <h3 className="panel__title">Garment Preferences</h3>
          </div>
          <div className="form-group">
            <label className="form-label">Preferred Garment Types</label>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {GARMENT_TYPES.map((g) => (
                <button
                  key={g}
                  type="button"
                  className={`filter-chip ${garmentTypes.includes(g) ? 'filter-chip--active' : ''}`}
                  onClick={() => setGarmentTypes(toggleArrayValue(garmentTypes, g))}
                >
                  {g.replace('-', ' ')}
                </button>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Preferred Garment Colors</label>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {GARMENT_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  className={`filter-chip ${garmentColors.includes(c) ? 'filter-chip--active' : ''}`}
                  onClick={() => setGarmentColors(toggleArrayValue(garmentColors, c))}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>

        {error && (
          <div className="form-error" style={{ marginBottom: '16px', fontSize: '0.9rem' }}>{error}</div>
        )}

        <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
          <button type="submit" className="btn btn--primary btn--lg" disabled={submitting}>
            {submitting ? 'Creating...' : 'Create Brief & Generate'}
          </button>
          <button type="button" className="btn btn--ghost btn--lg" onClick={() => navigate('/')}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}
